<template>
    <div class="preview" :style="pageStyle">
        <template v-if="content">
            <template v-if="isComponentPage">
                <div v-if="showHomeTitleBar" class="home-title-bar" :style="homeTitleBarStyle">
                    {{ homeTitleBarText }}
                </div>
                <div
                    v-for="(item, index) in components"
                    :key="item.id || index"
                    class="preview-item"
                    :class="{ selected: editable && selectedIndex === index }"
                    @click="selectItem(index)"
                >
                    <div v-if="editable && selectedIndex === index" class="preview-toolbar" @click.stop>
                        <span class="preview-toolbar-title">{{ getComponentLabel(item) }}</span>
                        <button type="button" @click="$emit('move', index, -1)">上移</button>
                        <button type="button" @click="$emit('move', index, 1)">下移</button>
                        <button type="button" @click="$emit('duplicate', index)">复制</button>
                        <button type="button" class="danger" @click="$emit('remove', index)">删除</button>
                    </div>
                    <div v-if="item.type === 'searchBar'" class="search">
                        <span class="search-icon">⌕</span>
                        <span>{{ getProp(item, 'placeholder', '搜索商品') }}</span>
                    </div>

                    <div v-else-if="item.type === 'heroSwiper'" class="swiper">
                        <img v-if="firstImage(item)" :src="resolveImage(firstImage(item))" />
                    </div>

                    <div v-else-if="item.type === 'iconNav'" class="nav-grid">
                        <div v-for="(nav, navIndex) in getItems(item)" :key="navIndex" class="nav-item">
                            <div class="nav-icon">
                                <img v-if="isImageNavIcon(nav)" :src="resolveImage(nav.iconImage)" />
                                <span v-else>{{ getNavIcon(nav) }}</span>
                            </div>
                            <div class="nav-label">{{ nav.label || nav.title || '入口' }}</div>
                        </div>
                    </div>

                    <div v-else-if="item.type === 'sectionTitle'" class="section-title">
                        <div>
                            <div class="section-main">{{ getProp(item, 'title', '精选推荐') }}</div>
                            <div class="section-sub">{{ getProp(item, 'subtitle', '') }}</div>
                        </div>
                        <div class="section-more" :class="{ clickable: hasLink(item) }">{{ getProp(item, 'moreText', '更多') }}</div>
                    </div>

                    <div v-else-if="item.type === 'noticeBar'" class="notice-bar" :class="{ clickable: hasLink(item) }">
                        {{ getProp(item, 'text', '活动通知') }}
                    </div>

                    <div v-else-if="item.type === 'imageBanner'" class="image-banner-grid" :style="getImageBannerGridStyle(item)">
                        <div
                            v-for="(banner, bannerIndex) in getImageBannerItems(item)"
                            :key="bannerIndex"
                            class="image-banner"
                            :class="{ clickable: hasLink({ props: banner }) }"
                            :style="getImageBannerItemStyle(item)"
                        >
                            <img v-if="banner.imageUrl" :src="resolveImage(banner.imageUrl)" />
                        </div>
                        <div v-if="!getImageBannerItems(item).length" class="image-banner image-banner-empty" :style="getImageBannerItemStyle(item)">请添加图片</div>
                    </div>

                    <div v-else-if="item.type === 'couponGrid'" class="coupon-grid">
                        <div class="block-title">{{ getProp(item, 'title', '领券专区') }}</div>
                        <div v-if="getItems(item).length" class="coupon-row">
                            <div v-for="(coupon, couponIndex) in getItems(item)" :key="couponIndex" class="coupon-card">
                                <div class="coupon-amount">{{ getCouponValue(coupon) }}</div>
                                <div class="coupon-text">{{ getCouponText(coupon) }}</div>
                                <div v-if="coupon.couponName" class="coupon-name">{{ coupon.couponName }}</div>
                            </div>
                        </div>
                        <div v-else class="coupon-empty">请选择系统优惠券</div>
                    </div>

                    <div v-else-if="item.type === 'promotionCard'" class="promotion-card" :class="{ clickable: hasLink(item) }">
                        <div class="promotion-copy">
                            <div class="promotion-title">{{ getProp(item, 'title', '活动推荐') }}</div>
                            <div class="promotion-sub">{{ getProp(item, 'subtitle', '热门活动专区') }}</div>
                            <div class="promotion-btn">{{ getProp(item, 'buttonText', '立即查看') }}</div>
                        </div>
                        <img v-if="getProp(item, 'imageUrl')" :src="resolveImage(getProp(item, 'imageUrl'))" class="promotion-image" />
                    </div>

                    <div v-else-if="item.type === 'richText'" class="rich-text" :class="{ clickable: hasLink(item) }" :style="{ textAlign: getProp(item, 'align', 'left') }">
                        <div v-if="getProp(item, 'title', '')" class="rich-title">{{ getProp(item, 'title', '') }}</div>
                        <div class="rich-content">{{ getProp(item, 'content', '文本说明区域') }}</div>
                    </div>

                    <div v-else-if="item.type === 'blankSpace'" class="blank-space" :style="{ height: `${getProp(item, 'height', 24)}px` }"></div>

                    <div v-else-if="item.type === 'goodsList'" class="goods-block">
                        <div class="block-title">{{ getProp(item, 'title', '商品列表') }}</div>
                        <div class="goods-grid">
                            <div v-for="goods in getGoodsPreviewItems(item)" :key="goods.goodsId || goods.index" class="goods-card">
                                <div class="goods-img">
                                    <img v-if="goods.image" :src="resolveImage(goods.image)" />
                                </div>
                                <div class="goods-name">{{ goods.goodsName }}</div>
                                <div class="goods-price">¥{{ getGoodsPrice(goods) }}</div>
                            </div>
                        </div>
                        <div v-if="!getGoodsPreviewItems(item).length" class="goods-empty">请选择要展示的商品</div>
                    </div>
                </div>
            </template>

            <template v-else-if="content.pageType === 'OPEN_CARD'">
                <div class="open-card-preview">
                    <div class="open-card-hero" :style="{ background: openCardHeroBackground }">
                        <div class="open-card-copy">
                            <div class="open-card-title">{{ config.heroTitle || '开通会员，享专属权益' }}</div>
                            <div class="open-card-subtitle">{{ config.heroSubtitle || config.summaryText || '会员价、积分、生日礼，更多福利持续解锁' }}</div>
                        </div>
                        <img v-if="showOpenCardBanner" :src="resolveImage(config.bannerUrl || config.cardImage)" class="open-card-banner" />
                    </div>
                    <div v-if="config.summaryText" class="open-card-summary">
                        {{ config.summaryText }}
                    </div>
                    <div v-if="config.featureList && config.featureList.length" class="open-card-benefits">
                        <div v-for="(feature, index) in config.featureList" :key="index" class="open-card-benefit">
                            <div class="open-card-benefit-icon" :style="{ backgroundColor: primaryColor }">{{ getFeatureIcon(feature) }}</div>
                            <div class="open-card-benefit-copy">
                                <div class="feature-title">{{ feature.title || feature.name }}</div>
                                <div class="feature-sub">{{ feature.subtitle || feature.desc }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="open-card-form">
                        <div class="open-card-form-title">填写开卡信息</div>
                        <div class="open-card-field">
                            <span>手机号码</span>
                            <em>点击获取</em>
                        </div>
                        <div v-if="config.showBirthDay !== false" class="open-card-field">
                            <span>生日</span>
                            <em>{{ config.mustInputBirth ? '必填' : '选填' }}</em>
                        </div>
                        <div v-if="config.showName !== false" class="open-card-field">
                            <span>姓名</span>
                            <em>{{ config.mustInputName ? '必填' : '选填' }}</em>
                        </div>
                        <div v-if="config.privacyText" class="open-card-privacy">{{ config.privacyText }}</div>
                    </div>
                    <button class="primary-btn" :style="{ backgroundColor: primaryColor }">{{ buttonText }}</button>
                </div>
            </template>

            <template v-else-if="content.pageType === 'USER_CENTER'">
                <div class="user-center-preview">
                    <div class="user-center-hero" :class="`user-center-hero-${config.cardMode || 'dark'}`" :style="{ background: userCenterHeroBackground }">
                        <div class="user-avatar">会</div>
                        <div class="user-hero-copy">
                            <div class="user-hero-title">{{ config.heroTitle || '我的会员中心' }}</div>
                            <div class="user-hero-subtitle">{{ config.heroSubtitle || config.summaryText || '查看订单、积分、余额和专属权益' }}</div>
                            <div class="user-level">黄金会员 · No. 888888</div>
                        </div>
                    </div>
                    <div v-if="config.summaryText" class="user-summary-card">{{ config.summaryText }}</div>
                    <div class="user-section-card">
                        <div class="user-section-head">
                            <span>{{ config.assetTitle || '我的资产' }}</span>
                        </div>
                        <div class="user-asset-grid">
                            <div v-if="config.showPoint !== false" class="user-asset-item">
                                <strong>1280</strong>
                                <span>积分</span>
                            </div>
                            <div v-if="config.showMoney !== false" class="user-asset-item">
                                <strong>¥200</strong>
                                <span>储值</span>
                            </div>
                            <div v-if="config.showCoupon !== false" class="user-asset-item">
                                <strong>3</strong>
                                <span>优惠券</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="config.showOrder !== false" class="user-section-card">
                        <div class="user-section-head">
                            <span>{{ config.orderTitle || '我的订单' }}</span>
                            <em v-if="config.showAllOrder !== false">全部订单</em>
                        </div>
                        <div class="user-order-grid">
                            <div v-for="item in ['待付款', '待发货', '待收货', '售后']" :key="item" class="user-order-item">
                                <div class="user-order-icon"></div>
                                <span>{{ item }}</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="config.showMenu !== false" class="user-section-card">
                        <div class="user-section-head">
                            <span>{{ config.menuTitle || '常用服务' }}</span>
                        </div>
                        <div class="user-menu-list">
                            <div v-for="(feature, index) in userCenterFeatures" :key="index" class="user-menu-item">
                                <div class="user-menu-icon" :style="{ backgroundColor: primaryColor }">{{ getFeatureIcon(feature) }}</div>
                                <div>
                                    <div class="feature-title">{{ feature.title || feature.name }}</div>
                                    <div class="feature-sub">{{ feature.subtitle || feature.desc }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template v-else-if="content.pageType === 'PRODUCT_DETAIL'">
                <div class="product-detail-preview">
                    <div class="product-main-image"></div>
                    <div class="product-info-card">
                        <div class="product-price-row">
                            <span class="product-price" :style="{ color: primaryColor }">¥199</span>
                            <span v-if="config.showStock !== false" class="product-stock" :style="{ color: primaryColor }">现货</span>
                        </div>
                        <div class="product-name">示例商品名称</div>
                        <div class="product-no">货号：GOKU-2026</div>
                        <div v-if="config.showStock !== false" class="product-summary">
                            <span>库存</span>
                            <strong>128</strong>
                        </div>
                    </div>
                    <div class="product-service">
                        <span>正品保障</span>
                        <span>门店同款</span>
                        <span>安心售后</span>
                    </div>
                    <div class="product-detail-title">商品详情</div>
                    <div class="product-detail-image"></div>
                    <button class="primary-btn" :style="{ backgroundColor: primaryColor }">加入购物车 / 立即购买</button>
                </div>
            </template>

            <template v-else>
                <div class="fixed-page">
                    <img v-if="config.bannerUrl || config.vipCardImage" :src="resolveImage(config.bannerUrl || config.vipCardImage)" class="banner" />
                    <div class="fixed-title">{{ pageTitle }}</div>
                    <div class="fixed-text">{{ fixedText }}</div>
                    <div v-if="config.featureList && config.featureList.length" class="feature-list">
                        <div v-for="(feature, index) in config.featureList" :key="index" class="feature-item">
                            <div class="feature-dot"></div>
                            <div class="feature-copy">
                                <div class="feature-title">{{ feature.title || feature.name }}</div>
                                <div class="feature-sub">{{ feature.subtitle || feature.desc }}</div>
                            </div>
                        </div>
                    </div>
                    <button class="primary-btn" :style="{ backgroundColor: buttonColor }">{{ buttonText }}</button>
                </div>
            </template>
        </template>
        <a-empty v-else description="草稿 JSON 无法预览" />
    </div>
</template>

<script>
import store from '@/store'
import { image } from '@/utils/Tool'

export default {
    name: 'MallV2Preview',
    props: {
        content: {
            type: Object,
            default: null
        },
        selectedIndex: {
            type: Number,
            default: -1
        },
        editable: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        components() {
            return this.content && this.content.components ? this.content.components : []
        },
        config() {
            return this.content && this.content.config ? this.content.config : {}
        },
        isComponentPage() {
            return this.content && (this.content.pageType === 'HOME' || this.isCustomPage(this.content.pageType))
        },
        pageStyle() {
            return {
                backgroundColor: this.config.backgroundColor || '#f7f7f7'
            }
        },
        pageTitle() {
            const titleMap = {
                PRODUCT_DETAIL: '商品详情',
                OPEN_CARD: '会员开卡',
                USER_CENTER: '个人中心',
                CART: '购物车'
            }
            return titleMap[this.content.pageType] || this.content.pageType
        },
        showHomeTitleBar() {
            return this.content && this.content.pageType === 'HOME' && this.config.showTitleBar !== false
        },
        homeTitleBarStyle() {
            return {
                backgroundColor: this.config.titleBarBackgroundColor || '#ffffff',
                color: this.config.titleBarTextColor || '#222222'
            }
        },
        homeTitleBarText() {
            if (this.config.titleMode === 'custom') {
                return this.config.pageTitle || this.config.storeShopName || '商城首页'
            }
            return this.config.storeShopName || this.config.pageTitle || '商城首页'
        },
        fixedText() {
            if (this.content.pageType === 'CART') {
                return this.config.emptyText || '购物车空空如也'
            }
            if (this.content.pageType === 'USER_CENTER') {
                return this.config.summaryText || '会员信息、订单和常用入口'
            }
            if (this.content.pageType === 'PRODUCT_DETAIL') {
                return this.config.showStock === false ? '商品详情页已隐藏库存展示' : '商品详情页展示库存信息'
            }
            return this.config.tipText || '配置开卡字段与会员权益'
        },
        buttonText() {
            return this.config.buttonText || this.config.goShoppingText || '立即进入'
        },
        buttonColor() {
            return this.config.buttonColor || '#D64545'
        },
        primaryColor() {
            return this.config.primaryColor || this.config.buttonColor || '#D64545'
        },
        showOpenCardBanner() {
            if (this.config.bannerMode === 'none') {
                return false
            }
            return this.config.showBanner !== false && (this.config.bannerUrl || this.config.cardImage)
        },
        openCardHeroBackground() {
            return `linear-gradient(135deg, ${this.primaryColor}, #2f3542)`
        },
        userCenterHeroBackground() {
            if (this.config.cardMode === 'light') {
                return `linear-gradient(135deg, #ffffff, ${this.config.backgroundColor || '#f7f7f7'})`
            }
            return `linear-gradient(135deg, ${this.primaryColor}, #2f3542)`
        },
        userCenterFeatures() {
            if (this.config.featureList && this.config.featureList.length) {
                return this.config.featureList
            }
            return [
                { title: '优惠资产', subtitle: '优惠券、积分和余额集中管理', icon: 'coupon' },
                { title: '会员权益', subtitle: '等级、生日礼和活动提醒', icon: 'gift' },
                { title: '个人资料', subtitle: '维护会员基础信息', icon: 'service' }
            ]
        }
    },
    methods: {
        selectItem(index) {
            if (this.editable) {
                this.$emit('select', index)
            }
        },
        getComponentLabel(item) {
            const labelMap = {
                searchBar: '搜索栏',
                heroSwiper: '轮播图',
                iconNav: '图标导航',
                sectionTitle: '标题栏',
                noticeBar: '公告栏',
                imageBanner: '图片横幅',
                couponGrid: '优惠券组',
                promotionCard: '促销卡片',
                richText: '富文本',
                blankSpace: '空白间距',
                goodsList: '商品列表'
            }
            return item && item.type ? (labelMap[item.type] || item.type) : '控件'
        },
        getProp(item, key, defaultValue) {
            return item && item.props && item.props[key] !== undefined ? item.props[key] : defaultValue
        },
        getItems(item) {
            return item && item.props && Array.isArray(item.props.items) ? item.props.items : []
        },
        isCustomPage(pageType) {
            return !!pageType && pageType.indexOf('CUSTOM_') === 0
        },
        getNavIcon(nav) {
            if (!nav) {
                return '•'
            }
            const iconMap = {
                new: '新',
                hot: '热',
                category: '类',
                coupon: '券',
                vip: '会',
                order: '单',
                cart: '车',
                recharge: '充',
                store: '店',
                service: '客',
                circle: '•'
            }
            if (nav.icon && iconMap[nav.icon]) {
                return iconMap[nav.icon]
            }
            if (nav.icon) {
                return nav.icon.slice(0, 1).toUpperCase()
            }
            return '•'
        },
        isImageNavIcon(nav) {
            return nav && nav.iconType === 'image' && nav.iconImage
        },
        getCouponValue(coupon) {
            if (!coupon) {
                return ''
            }
            if (coupon.couponValue) {
                return coupon.couponValue
            }
            return `¥${coupon.amount || coupon.value || 0}`
        },
        getCouponText(coupon) {
            if (coupon && coupon.couponLabel) {
                return coupon.couponLabel
            }
            if (coupon && coupon.title) {
                return coupon.title
            }
            return `满${coupon && coupon.threshold ? coupon.threshold : 0}可用`
        },
        getImageBannerItems(item) {
            const items = this.getItems(item)
            if (items.length) {
                return items
            }
            const imageUrl = this.getProp(item, 'imageUrl', '')
            if (!imageUrl) {
                return []
            }
            return [{
                imageUrl,
                linkType: this.getProp(item, 'linkType', 'none'),
                linkTarget: this.getProp(item, 'linkTarget', ''),
                linkName: this.getProp(item, 'linkName', '')
            }]
        },
        getImageBannerGridStyle(item) {
            const columns = Math.min(Math.max(Number(this.getProp(item, 'columns', 1)) || 1, 1), 3)
            return {
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gap: `${this.getProp(item, 'gap', 8)}px`
            }
        },
        getImageBannerItemStyle(item) {
            return {
                borderRadius: `${this.getProp(item, 'radius', 16)}px`,
                aspectRatio: this.getCssAspectRatio(this.getProp(item, 'aspectRatio', '16:9'))
            }
        },
        getCssAspectRatio(value) {
            const ratioMap = {
                '16:9': '16 / 9',
                '3:1': '3 / 1',
                '1:1': '1 / 1',
                '4:3': '4 / 3'
            }
            return ratioMap[value] || '16 / 9'
        },
        getGoodsPreviewItems(item) {
            const dataSource = item && item.dataSource ? item.dataSource : {}
            const snapshots = Array.isArray(dataSource.goodsSnapshots) ? dataSource.goodsSnapshots : []
            if (dataSource.mode === 'manual') {
                const manualGoods = snapshots.length ? snapshots : (Array.isArray(dataSource.goodsIds) ? dataSource.goodsIds.map(goodsId => ({ goodsId })) : [])
                return manualGoods.map(goods => ({
                    goodsId: goods.goodsId,
                    goodsName: goods.goodsName || goods.goodsId || '未命名商品',
                    image: goods.image || goods.imageUrl,
                    unitPrice: goods.unitPrice,
                    retailPrice: goods.retailPrice
                }))
            }
            return Array.from({ length: Math.min(this.getProp(item, 'showCount', 4), 6) }).map((goods, index) => ({
                index,
                goodsName: `示例商品 ${index + 1}`,
                unitPrice: 99
            }))
        },
        getGoodsPrice(goods) {
            const price = goods && (goods.unitPrice !== undefined && goods.unitPrice !== null ? goods.unitPrice : goods.retailPrice)
            return price !== undefined && price !== null && price !== '' ? price : '0'
        },
        hasLink(item) {
            return item && item.props && item.props.linkType && item.props.linkType !== 'none' && item.props.linkTarget
        },
        firstImage(item) {
            const items = this.getItems(item)
            return items.length > 0 ? items[0].imageUrl : ''
        },
        resolveImage(imageUrl) {
            if (!imageUrl) {
                return ''
            }
            if (imageUrl.indexOf('http://') === 0 || imageUrl.indexOf('https://') === 0) {
                return imageUrl
            }
            if (imageUrl.indexOf('/img/') === 0 || imageUrl.indexOf('/static/') === 0) {
                return imageUrl
            }
            if (!store.getters.image) {
                return imageUrl
            }
            return image(imageUrl)
        },
        getFeatureIcon(feature) {
            const iconMap = {
                price: '价',
                point: '积',
                birthday: '礼',
                gift: '礼',
                service: '享'
            }
            return iconMap[feature && feature.icon] || '享'
        }
    }
}
</script>

<style scoped>
.preview {
    flex: 1;
    min-height: 0;
    padding: 12px;
    overflow-y: auto;
    overflow-x: hidden;
}

.preview-item {
    position: relative;
    margin-bottom: 12px;
    padding: 4px;
    border: 1px solid transparent;
    border-radius: 14px;
    cursor: pointer;
}

.preview-item:hover {
    border-color: #d9e8ff;
    background: rgba(24, 144, 255, 0.04);
}

.preview-item.selected {
    border-color: #1890ff;
    background: rgba(24, 144, 255, 0.08);
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.12);
}

.preview-toolbar {
    position: sticky;
    top: 0;
    z-index: 3;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    margin-bottom: 6px;
    padding: 5px 6px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 4px 12px rgba(15, 35, 80, 0.12);
}

.preview-toolbar-title {
    max-width: 72px;
    overflow: hidden;
    color: #1f2d3d;
    font-size: 12px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.preview-toolbar button {
    height: 22px;
    padding: 0 6px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    background: #ffffff;
    color: #333333;
    font-size: 12px;
    cursor: pointer;
}

.preview-toolbar button:hover {
    border-color: #1890ff;
    color: #1890ff;
}

.preview-toolbar button.danger:hover {
    border-color: #ff4d4f;
    color: #ff4d4f;
}

.home-title-bar {
    height: 44px;
    line-height: 44px;
    margin: -12px -12px 12px -12px;
    padding: 0 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
    font-size: 16px;
    font-weight: 600;
    text-align: center;
}

.search {
    display: flex;
    align-items: center;
    height: 36px;
    padding: 0 16px;
    border-radius: 18px;
    background: #ffffff;
    color: #999999;
    cursor: pointer;
}

.search-icon {
    margin-right: 6px;
    color: #b6bec8;
}

.swiper {
    height: 170px;
    overflow: hidden;
    border-radius: 12px;
    background: #eeeeee;
}

.image-banner-grid {
    display: grid;
}

.image-banner {
    min-height: 74px;
    overflow: hidden;
    background: #eeeeee;
}

.swiper img,
.image-banner img,
.banner {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-banner-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9aa3af;
    font-size: 13px;
}

.nav-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    padding: 12px 8px;
    border-radius: 12px;
    background: #ffffff;
}

.nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
}

.nav-icon {
    width: 44px;
    height: 44px;
    border-radius: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    background: linear-gradient(135deg, #ff7a59, #ffb36b);
    font-weight: 700;
}

.nav-icon img {
    width: 100%;
    height: 100%;
    border-radius: 22px;
    object-fit: cover;
}

.nav-label {
    font-size: 12px;
    color: #333333;
}

.section-title {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    padding: 2px 4px;
}

.section-main {
    font-size: 16px;
    font-weight: 700;
    color: #222222;
}

.section-sub {
    margin-top: 2px;
    font-size: 12px;
    color: #999999;
}

.section-more {
    font-size: 12px;
    color: #d64545;
}

.section-more.clickable::after {
    content: "›";
    margin-left: 3px;
}

.notice-bar {
    padding: 10px 12px;
    border-radius: 12px;
    background: linear-gradient(135deg, #fff3ed, #fff9f2);
    color: #d64545;
    font-size: 13px;
}

.section-more.clickable,
.notice-bar.clickable {
    cursor: pointer;
}

.notice-bar.clickable::after {
    content: "›";
    float: right;
}

.goods-block,
.coupon-grid {
    margin-top: 2px;
}

.block-title {
    margin-bottom: 8px;
    font-weight: 700;
    color: #222222;
}

.coupon-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.coupon-card {
    padding: 10px 8px;
    border-radius: 12px;
    background: linear-gradient(180deg, #fff4ec, #ffffff);
    border: 1px solid #ffe0cc;
    text-align: center;
}

.coupon-amount {
    font-size: 18px;
    font-weight: 700;
    color: #d64545;
}

.coupon-text {
    margin-top: 4px;
    font-size: 12px;
    color: #8a4a2f;
}

.coupon-name {
    margin-top: 4px;
    overflow: hidden;
    color: #666666;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.coupon-empty {
    padding: 18px 12px;
    border: 1px dashed #ffd8bf;
    border-radius: 12px;
    background: #fff9f5;
    color: #d46b08;
    font-size: 12px;
    text-align: center;
}

.promotion-card {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 12px;
    border-radius: 16px;
    background: linear-gradient(135deg, #fff6f6, #fff);
    border: 1px solid #f0d6d6;
}

.image-banner.clickable,
.promotion-card.clickable,
.rich-text.clickable {
    cursor: pointer;
}

.promotion-copy {
    flex: 1;
}

.promotion-title {
    font-size: 16px;
    font-weight: 700;
    color: #222222;
}

.promotion-sub {
    margin-top: 4px;
    color: #777777;
    font-size: 12px;
}

.promotion-btn {
    display: inline-block;
    margin-top: 10px;
    padding: 6px 12px;
    border-radius: 999px;
    background: #d64545;
    color: #ffffff;
    font-size: 12px;
}

.promotion-image {
    width: 96px;
    height: 96px;
    border-radius: 12px;
    object-fit: cover;
    background: #eeeeee;
}

.rich-text {
    padding: 12px;
    border-radius: 12px;
    background: #ffffff;
    color: #555555;
}

.rich-title {
    margin-bottom: 6px;
    color: #222222;
    font-weight: 700;
}

.rich-content {
    line-height: 1.7;
    white-space: pre-wrap;
}

.blank-space {
    width: 100%;
}

.goods-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.goods-card {
    padding: 8px;
    border-radius: 12px;
    background: #ffffff;
}

.goods-img {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    border-radius: 10px;
    background: linear-gradient(135deg, #eeeeee, #dddddd);
    overflow: hidden;
}

.goods-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.goods-name {
    margin-top: 8px;
    overflow: hidden;
    color: #333333;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.goods-price {
    color: #d64545;
    font-weight: 700;
}

.goods-empty {
    padding: 18px;
    border-radius: 12px;
    background: #ffffff;
    color: #9aa3af;
    font-size: 13px;
    text-align: center;
}

.fixed-page {
    padding: 18px;
}

.user-center-preview {
    padding: 14px;
}

.user-center-hero {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 148px;
    padding: 18px;
    border-radius: 20px;
    color: #ffffff;
    box-shadow: 0 12px 26px rgba(31, 41, 55, 0.14);
}

.user-center-hero-light {
    color: #222222;
    border: 1px solid #f0f0f0;
}

.user-avatar {
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.22);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
}

.user-center-hero-light .user-avatar {
    background: #f5f7fa;
}

.user-hero-copy {
    flex: 1;
    min-width: 0;
}

.user-hero-title {
    font-size: 20px;
    line-height: 1.3;
    font-weight: 700;
}

.user-hero-subtitle {
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.6;
    opacity: 0.82;
}

.user-level {
    display: inline-block;
    margin-top: 12px;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
    font-size: 12px;
}

.user-center-hero-light .user-level {
    background: #f5f7fa;
}

.user-summary-card,
.user-section-card {
    margin-top: 14px;
    padding: 14px;
    border-radius: 16px;
    background: #ffffff;
}

.user-summary-card {
    color: #555555;
    font-size: 13px;
    line-height: 1.6;
}

.user-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    color: #222222;
    font-weight: 700;
}

.user-section-head em {
    color: #999999;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
}

.user-asset-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.user-asset-item {
    padding: 10px 6px;
    border-radius: 12px;
    background: #f7f9fc;
    text-align: center;
}

.user-asset-item strong {
    display: block;
    color: #222222;
}

.user-asset-item span,
.user-order-item span {
    color: #888888;
    font-size: 12px;
}

.user-order-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
}

.user-order-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
}

.user-order-icon {
    width: 34px;
    height: 34px;
    border-radius: 12px;
    background: #f2f5f8;
}

.user-menu-list {
    display: grid;
    gap: 10px;
}

.user-menu-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.user-menu-icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: 17px;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
}

.open-card-preview {
    padding: 14px;
}

.open-card-hero {
    min-height: 178px;
    padding: 18px;
    border-radius: 20px;
    color: #ffffff;
    overflow: hidden;
    position: relative;
}

.open-card-copy {
    position: relative;
    z-index: 1;
    max-width: 210px;
}

.open-card-title {
    font-size: 22px;
    line-height: 1.25;
    font-weight: 700;
}

.open-card-subtitle {
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.6;
    opacity: 0.88;
}

.open-card-banner {
    position: absolute;
    right: 14px;
    bottom: 14px;
    width: 118px;
    height: 92px;
    border-radius: 14px;
    object-fit: cover;
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
}

.open-card-summary {
    margin-top: 14px;
    padding: 12px 14px;
    border-radius: 14px;
    background: #ffffff;
    color: #555555;
    font-size: 13px;
    line-height: 1.6;
}

.open-card-benefits {
    margin-top: 14px;
    display: grid;
    gap: 10px;
}

.open-card-benefit {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    border-radius: 14px;
    background: #ffffff;
}

.open-card-benefit-icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: 17px;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
}

.open-card-benefit-copy {
    flex: 1;
    min-width: 0;
}

.open-card-form {
    margin-top: 14px;
    padding: 14px;
    border-radius: 16px;
    background: #ffffff;
}

.open-card-form-title {
    margin-bottom: 8px;
    color: #222222;
    font-weight: 700;
}

.open-card-field {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
    color: #333333;
    font-size: 13px;
}

.open-card-field em {
    color: #999999;
    font-style: normal;
}

.open-card-privacy {
    margin-top: 10px;
    color: #999999;
    font-size: 12px;
    line-height: 1.5;
}

.product-detail-preview {
    padding-bottom: 14px;
}

.product-main-image {
    height: 260px;
    background: linear-gradient(135deg, #f2f4f7, #ffffff);
    border-bottom: 1px solid #eeeeee;
}

.product-info-card,
.product-service {
    margin: 12px 14px 0;
    padding: 14px;
    border-radius: 14px;
    background: #ffffff;
}

.product-price-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.product-price {
    font-size: 28px;
    line-height: 1;
    font-weight: 800;
}

.product-stock {
    padding: 4px 10px;
    border-radius: 14px;
    background: #f5f7f9;
    font-size: 12px;
    font-weight: 700;
}

.product-name {
    margin-top: 12px;
    color: #222222;
    font-size: 16px;
    line-height: 1.4;
    font-weight: 700;
}

.product-no {
    margin-top: 6px;
    color: #999999;
    font-size: 12px;
}

.product-summary {
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f7f8fa;
    display: flex;
    justify-content: center;
    gap: 8px;
    color: #777777;
}

.product-summary strong {
    color: #222222;
}

.product-service {
    display: flex;
    justify-content: space-between;
    color: #777777;
    font-size: 12px;
}

.product-detail-title {
    margin-top: 16px;
    padding: 12px 0;
    background: #ffffff;
    text-align: center;
    color: #222222;
    font-weight: 700;
}

.product-detail-image {
    height: 180px;
    background: linear-gradient(180deg, #ffffff, #f2f4f7);
}

.banner {
    height: 160px;
    border-radius: 14px;
}

.fixed-title {
    margin-top: 20px;
    font-size: 20px;
    font-weight: 700;
}

.fixed-text {
    margin-top: 8px;
    color: #777777;
}

.feature-list {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.feature-item {
    display: flex;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 12px;
    background: #ffffff;
}

.feature-dot {
    width: 10px;
    height: 10px;
    margin-top: 6px;
    border-radius: 50%;
    background: #d64545;
    flex-shrink: 0;
}

.feature-copy {
    flex: 1;
}

.feature-title {
    font-size: 14px;
    font-weight: 600;
    color: #222222;
}

.feature-sub {
    margin-top: 2px;
    font-size: 12px;
    color: #888888;
}

.primary-btn {
    width: 100%;
    height: 42px;
    margin-top: 24px;
    border: 0;
    border-radius: 21px;
    color: #ffffff;
    font-weight: 600;
}
</style>
